/**
 * Service Layer - Transcription Service
 * Handles transcription and translation logic
 * Uses Core layer for Azure Speech SDK operations
 * Updates State layer (store)
 */

import {TranscriptionManager} from '../core/transcription/TranscriptionManager';
import {messageTypeEnum, TRANSCRIPTION_MESSAGE_LIFETIME} from '../core/constants/RtcEnums';
import {translationLanguageEnum} from '../../../serverClientShared/sharedEnums';
import Utility from '../../../Utility';

export class TranscriptionService {
    constructor(store, socketManager) {
        this.store = store;
        this.socketManager = socketManager;
        this.transcriptionManager = new TranscriptionManager();
        this.cleanupInterval = null;
    }

    /**
     * Start transcription
     */
    async startTranscription() {
        try {
            // Close existing recognizer if any
            if (this.transcriptionManager.getRecognizer()) {
                await this.transcriptionManager.close();
            }

            // Detect language if not set
            if (!this.store.transcriptionLanguage) {
                await this.detectLanguage();
            }

            // Create translation recognizer
            const recognizer = this.transcriptionManager.createTranslationRecognizer(
                this.store.transcriptionToken,
                this.store.transcriptionRegion,
                this.store.transcriptionLanguage,
                Object.values(this.store.transcriptionLanguages)
            );

            // Set up recognition handler
            this.transcriptionManager.onRecognized((sender, event) => {
                try {
                    const {translations} = event.result;
                    this.sendTranscriptionMessage(translations);
                } catch (e) {
                    console.error('could not transcribe', e);
                }
            });

            // Start recognition if mic is on
            if (this.store.micOn) {
                this.transcriptionManager.startContinuousRecognition(
                    () => {
                        console.log('Transcription started');
                    },
                    (e) => {
                        console.error('could not startContinuousRecognitionAsync', e);
                    }
                );
            }

            // Start cleanup interval
            if (!this.cleanupInterval) {
                this.cleanupInterval = setInterval(() => {
                    this.cleanupOldTranscriptions();
                }, 1000);
            }

            this.store.translationRecognizer = recognizer;
        } catch (e) {
            console.error('Error during transcription setup:', e);
            this.store.translationRecognizer = null;
        }
    }

    /**
     * Stop transcription
     */
    stopTranscription() {
        this.transcriptionManager.stopContinuousRecognition();

        if (this.cleanupInterval) {
            clearInterval(this.cleanupInterval);
            this.cleanupInterval = null;
        }
    }

    /**
     * Toggle transcription on/off
     */
    async toggleTranscription() {
        if (this.store.micOn && this.transcriptionManager.getRecognizer()) {
            if (this.store.transcriptionOn) {
                this.transcriptionManager.startContinuousRecognition();
            } else {
                this.transcriptionManager.stopContinuousRecognition();
            }
        }
    }

    /**
     * Detect language from audio
     */
    async detectLanguage() {
        try {
            const languagesArray = Object.values(this.store.transcriptionLanguages);
            const detectedLanguage = await this.transcriptionManager.detectLanguage(
                this.store.transcriptionToken,
                this.store.transcriptionRegion,
                languagesArray
            );

            if (detectedLanguage) {
                this.store.transcriptionLanguage = detectedLanguage;
            } else {
                this.store.transcriptionLanguage = 'en-US';
            }
        } catch (e) {
            console.error('Error detecting language:', e);
            this.store.transcriptionLanguage = 'en-US';
        }
    }

    /**
     * Send transcription message to other participants
     */
    sendTranscriptionMessage(translations = new Map()) {
        const translationsMessage = {};

        // Convert translations map to object with all target languages
        Object.values(translationLanguageEnum).forEach(lang => {
            let translation = translations.get(lang);

            if (!translation) {
                const shortLang = lang.split('-')[0];
                translation = translations.get(shortLang);

                if (!translation && shortLang === 'zh') {
                    translation = translations.get('zh-Hans');
                }
            }

            translationsMessage[lang] = translation;
        });

        // Add to own transcription messages
        this.addTranscriptionMessage({
            Translations: translationsMessage,
            Sender: {
                id: this.store.deviceId
            }
        });

        // Send to other participants
        const transcriptionMessage = JSON.stringify({
            Translations: translationsMessage,
            MessageId: messageTypeEnum.TRANSCRIPTION_MESSAGE,
            Sender: {
                id: this.store.deviceId,
                Name: this.store.name,
                LastName: this.store.userDetails?.LastName
            }
        });

        this.socketManager.sendRoomMessage(this.store.roomId, transcriptionMessage, this.store.deviceId);
    }

    /**
     * Add received transcription message
     */
    addTranscriptionMessage(message) {
        const participantId = message.Sender?.id;
        const participant = this.store.participants[participantId] || {};
        const maxRowNumber = Utility.isMobile() ? 2 : 4;

        if (this.store.transcriptionMessages.length === maxRowNumber) {
            this.store.transcriptionMessages.shift();
        }

        const text = message.Translations[this.store.transcriptionLanguage];
        if (!text) {
            return;
        }

        this.store.transcriptionMessages.push({
            time: Date.now(),
            participant,
            text
        });
    }

    /**
     * Cleanup old transcription messages
     */
    cleanupOldTranscriptions() {
        for (let i = this.store.transcriptionMessages.length - 1; i >= 0; i--) {
            const message = this.store.transcriptionMessages[i];
            if (message.time < Date.now() - TRANSCRIPTION_MESSAGE_LIFETIME) {
                this.store.transcriptionMessages.splice(i, 1);
            }
        }
    }

    /**
     * Update transcription token (for token refresh)
     */
    updateToken(newToken) {
        this.store.transcriptionToken = newToken;
        this.transcriptionManager.updateToken(newToken);
    }

    requestTranscriptionFromPeers() {
        try {
            const message = JSON.stringify({
                MessageId: messageTypeEnum.TRANSCRIPTION_REQUEST,
            });
            this.socketManager.sendRoomMessage(this.store.roomId, message, this.store.deviceId);
        } catch (error) {
            console.error('Failed to request transcription from peers:', error);
        }
    }

    async handleTranscriptionRequest() {
        if (this.transcriptionManager.getRecognizer() || !this.store.transcriptionToken) {
            return;
        }

        // Set default language if not already set to avoid blocking on detectLanguage
        if (!this.store.transcriptionLanguage) {
            this.store.transcriptionLanguage = 'en-US';
        }

        await this.startTranscription();
    }

    /**
     * Change transcription language
     */
    async changeLanguage(newLanguage) {
        this.store.transcriptionLanguage = newLanguage;
        await this.startTranscription();
    }

    /**
     * Cleanup
     */
    async cleanup() {
        this.stopTranscription();
        await this.transcriptionManager.close();

        if (this.cleanupInterval) {
            clearInterval(this.cleanupInterval);
            this.cleanupInterval = null;
        }
    }
}
